/**
 * Utilitaire de gestion des rôles dans l'application
 * Permet de convertir les rôles API vers les rôles UI et vice versa
 */

// Rôles côté API
export type ApiRole =
  | 'responsable_achat'
  | 'validateur'
  | 'acheteur'
  | 'gestionnaire'
  | 'interlocuteur'
  | 'signataire';

// Rôles côté UI
export type UiRole =
  | 'Acheteur signataire admin'
  | 'Acheteur signataire'
  | 'Acheteur'
  | 'Prestataire signataire admin'
  | 'Prestataire signataire'
  | 'Prestataire';

// Conversion API -> UI
const apiToUiRoleMap: Record<ApiRole, UiRole> = {
  'responsable_achat': 'Acheteur signataire admin',
  'validateur': 'Acheteur signataire',
  'acheteur': 'Acheteur',
  'gestionnaire': 'Prestataire signataire admin',
  'signataire': 'Prestataire signataire',
  'interlocuteur': 'Prestataire'
};

// Conversion UI -> API
const uiToApiRoleMap: Record<UiRole, ApiRole> = {
  'Acheteur signataire admin': 'responsable_achat',
  'Acheteur signataire': 'validateur',
  'Acheteur': 'acheteur',
  'Prestataire signataire admin': 'gestionnaire',
  'Prestataire signataire': 'signataire',
  'Prestataire': 'interlocuteur'
};

/**
 * Convertit un rôle API en rôle UI
 * @param apiRole - Rôle provenant de l'API
 * @returns Rôle formaté pour l'UI
 */
export const convertApiRoleToUiRole = (apiRole: string): string => {
  return apiToUiRoleMap[apiRole as ApiRole] || apiRole;
};

/**
 * Convertit un rôle UI en rôle API
 * @param uiRole - Rôle de l'interface utilisateur
 * @returns Rôle formaté pour l'API
 */
export const convertUiRoleToApiRole = (uiRole: string): string => {
  return uiToApiRoleMap[uiRole as UiRole] || uiRole;
};

/**
 * Vérifie si l'utilisateur est un acheteur (côté client)
 * @param role - Rôle de l'utilisateur (format API)
 */
export const isClientRole = (role: string) => {
  if (!role) return false;

  // Convertir le rôle en minuscules pour une comparaison insensible à la casse
  const roleLowerCase = role.toLowerCase();

  // Liste des rôles client (tous en minuscules)
  const clientRoles = [
    'acheteur',
    'acheteur admin',
    'acheteur signataire',
    'acheteur signataire admin',
    'validateur',
    'responsable_achat',
    'responsable achat'
  ];

  // Vérifier si le rôle (en minuscules) est dans la liste des rôles client
  return clientRoles.some(clientRole => roleLowerCase.includes(clientRole));
};

/**
 * Vérifie si l'utilisateur est un prestataire (côté fournisseur)
 * @param role - Rôle de l'utilisateur (format API)
 */
export const isPrestataireRole = (role: string) => {
  if (!role) return false;

  // Convertir le rôle en minuscules pour une comparaison insensible à la casse
  const roleLowerCase = role.toLowerCase();

  // Liste des rôles prestataire (tous en minuscules)
  const prestataireRoles = [
    'prestataire',
    'prestataire admin',
    'prestataire signataire',
    'prestataire signataire admin',
    'gestionnaire',
    'interlocuteur'
  ];

  // Vérifier si le rôle (en minuscules) est dans la liste des rôles prestataire
  return prestataireRoles.some(prestataireRole => roleLowerCase.includes(prestataireRole));
};

/**
 * Vérifie si l'utilisateur est un administrateur (responsable_achat ou gestionnaire)
 * @param role - Rôle de l'utilisateur (format API)
 */
export const isAdminRole = (role: string): boolean => {
  if (!role) return false;

  // Convertir le rôle en minuscules pour une comparaison insensible à la casse
  const roleLowerCase = role.toLowerCase();

  // Liste combinée des rôles admin (API et UI)
  const adminRoles = [
    'responsable_achat',         // Format API
    'gestionnaire',              // Format API
    'acheteur signataire admin', // Format UI
    'prestataire signataire admin', // Format UI
    'responsable achat'          // Variation possible
  ];

  // Vérifier si le rôle correspond à l'un des rôles admin
  return adminRoles.some(adminRole => roleLowerCase.includes(adminRole));
};